import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CloseIcon from "@mui/icons-material/Close";
import snakeCase from "lodash/snakeCase";
import { fullWidthDialog } from "src/constants/dialog";
import { useQuery, useApolloClient } from "@apollo/client/react";
import useMutation from "src/hooks/useMutation";
import useQueryParams from "src/hooks/useQueryParams";
import useNotification from "src/hooks/notification";
import { useFieldSettings } from "src/contexts/customFields";
import { isFieldSettingEnabled } from "src/utils/fieldSettings";
import assetsTableConfig from "src/constants/tablePro/assets";
import useTablePro from "src/components/TablePro/extensions/useTablePro";
import Order from "src/components/TablePro/Order";
import ResizeBorder from "src/components/TablePro/TableHeader/ResizeBorder";
import {
  createCustomFieldPayload,
  findExistingCustomField,
  getCustomFieldTemplateId,
} from "src/components/TablePro/Table/utils";
import {
  CREATE_ASSET_MUTATION,
  CREATE_SKU_MUTATION,
  ADD_SHIPMENT_ITEMS_MUTATION,
  UPDATE_ASSET_MUTATION,
  ASSETS_BULK_UPDATE_MUTATION,
} from "src/constants/graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import { ONSITE_MANIFEST_COLLECTION_QUERY, GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";
import { fetchAssetValues } from "src/utils/asset";
import { useNavigationEffect } from "src/utils/keyEffects";
import { AssetsCollectionProvider } from "src/contexts/assetsCollection";
import { EditAssetProvider } from "src/components/TablePro/Table/EditAssetProvider";
import AssetDetailsModal from "src/components/TablePro/components/assets/AssetDetailsModal";

const COLUMN_WIDTHS = {
  barcode: 160,
};

const MIN_COL_WIDTH = 100;
const DEFAULT_COL_WIDTH = 120;

const CHECKBOX_COL_WIDTH = 48;
const QTY_COL_WIDTH = 72;
const PAGE_LIMIT = 40;
const SCROLL_THRESHOLD = 200;
const SKELETON_ROWS = 6;

const stickyCheckboxSx = {
  position: "sticky",
  left: 0,
  width: CHECKBOX_COL_WIDTH,
  minWidth: CHECKBOX_COL_WIDTH,
  maxWidth: CHECKBOX_COL_WIDTH,
  zIndex: 1,
};

const stickyQtySx = {
  position: "sticky",
  left: CHECKBOX_COL_WIDTH,
  width: QTY_COL_WIDTH,
  minWidth: QTY_COL_WIDTH,
  maxWidth: QTY_COL_WIDTH,
  zIndex: 1,
  boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
  borderRight: "2px solid",
  borderRightColor: "divider",
  textAlign: "center",
  px: 0.5,
};

const stickyBg = (theme, isSelected) =>
  isSelected
    ? `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected}), ${theme.palette.background.paper}`
    : theme.palette.background.paper;

const SKIP_FIELD_IDS = new Set([
  "BULK_CHECKBOX",
  "photoUpload",
  "photoCarousel",
  "client.name",
  "project.name",
  "rackLocations",
  "availableQuantity",
  "warehouseQuantity",
  "inCartQuantity",
  "reservedQuantity",
  "allocatedQuantity",
  "warehousesFromRackLocations",
  "receivingReportNumber",
  "outboundSummary",
  "attachments",
  "lastIn",
  "lastOut",
  "createdAt",
  "updatedAt",
  "daysLeftToExpire",
]);

const editInputStyles = {
  width: "100%",
  border: "2px solid",
  borderColor: "primary.main",
  borderRadius: 0.5,
  outline: "none",
  background: "transparent",
  font: "inherit",
  fontSize: "0.8125rem",
  px: 0.5,
  py: 0.25,
  boxSizing: "border-box",
  textAlign: "inherit",
};

function EditableCell({ value, onSave, inputType = "text", activateWithKey }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      if (!activateWithKey) inputRef.current?.select();
    }
  }, [editing]);

  useEffect(() => {
    if (activateWithKey) {
      setEditVal(activateWithKey.key);
      setEditing(true);
    }
  }, [activateWithKey]);

  const save = () => {
    setEditing(false);
    const parsed = inputType === "number"
      ? (editVal === "" || editVal === null ? 0 : Number(editVal))
      : editVal;
    if (parsed !== value) onSave(parsed);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    if (e.key === "Escape") { setEditVal(value ?? ""); setEditing(false); }
  };

  if (editing) {
    return (
      <Box
        component="input"
        ref={inputRef}
        data-skip-navigation-effect="true"
        type={inputType}
        value={editVal ?? ""}
        onChange={e => setEditVal(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        sx={editInputStyles}
      />
    );
  }

  return (
    <Box
      onClick={() => { setEditVal(value ?? ""); setEditing(true); }}
      sx={{
        cursor: "text",
        minHeight: 20,
        width: "100%",
        borderRadius: 0.5,
        px: 0.5,
        textAlign: "inherit",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {(value !== null && value !== undefined && value !== "") ? String(value) : "—"}
    </Box>
  );
}

const getFieldLabel = field => typeof field.label === "string" ? field.label : field.id;

export default function OnsiteManifestModal({ project, onClose }) {
  const params = useQueryParams();
  const shipmentId = params.get("id");
  const shipmentCode = params.get("shipmentId");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [validating, setValidating] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("item_id");
  const [sortDir, setSortDir] = useState("asc");
  const [entities, setEntities] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [detailEntity, setDetailEntity] = useState(null);
  const apolloClient = useApolloClient();
  const { notifySuccess, notifyError } = useNotification();

  const fieldSettings = useFieldSettings(project);
  const { fields: tableProFields, updateSettings: updateTableSettings } = useTablePro({
    key: "assetsTableSettings",
    default: assetsTableConfig,
  });

  const orderVar = useMemo(() => ({ [sortField]: sortDir }), [sortField, sortDir]);

  const queryVars = useMemo(() => ({
    projectId: project.id,
    pendingValidation: true,
    page: 1,
    limit: PAGE_LIMIT,
    order: orderVar,
    ...(searchTerm && { search: searchTerm }),
  }), [project.id, searchTerm, orderVar]);

  const { data, loading, refetch, fetchMore } = useQuery(ONSITE_MANIFEST_COLLECTION_QUERY, {
    variables: queryVars,
  });

  const collection = data?.assetsCollection?.collection || [];
  const metadata = data?.assetsCollection?.metadata || {};
  const hasMore = (metadata.currentPage || 0) < (metadata.totalPages || 0);

  const flattenSku = useCallback(s => {
    const flattened = { ...s, validationQty: 1 };
    (s.customFields || []).forEach(cf => {
      if (cf.customFieldTemplate?.id) {
        flattened[`customField${cf.customFieldTemplate.id}`] = cf.value;
      }
    });
    return flattened;
  }, []);

  const pinnedItemsRef = useRef([]);

  useEffect(() => {
    setEntities(prev => {
      const prevQtyMap = new Map(prev.map(e => [e.id, e.validationQty]));
      const collectionIds = new Set(collection.map(s => s.id));
      const pinned = pinnedItemsRef.current
        .filter(p => !collectionIds.has(p.id))
        .map(p => {
          const prevQty = prevQtyMap.get(p.id);
          return prevQty !== undefined ? { ...p, validationQty: prevQty } : p;
        });
      const fromQuery = collection.map(s => {
        const flattened = flattenSku(s);
        const prevQty = prevQtyMap.get(s.id);
        if (prevQty !== undefined) flattened.validationQty = prevQty;
        return flattened;
      });
      return [...pinned, ...fromQuery];
    });
  }, [collection]);

  const [createAsset] = useMutation(CREATE_ASSET_MUTATION);
  const [createSku] = useMutation(CREATE_SKU_MUTATION);
  const [addShipmentItem] = useMutation(ADD_SHIPMENT_ITEMS_MUTATION);
  const [updateAsset] = useMutation(UPDATE_ASSET_MUTATION);
  const [bulkUpdateAssets] = useMutation(ASSETS_BULK_UPDATE_MUTATION);

  const customFieldColumns = useMemo(() => {
    if (!fieldSettings) return [];
    const first = data?.assetsCollection?.collection?.[0];
    if (!first?.customFields) return [];
    return first.customFields
      .filter(cf => cf.customFieldTemplate?.id && isFieldSettingEnabled(fieldSettings[`custom_field_${cf.customFieldTemplate.id}`]))
      .map(cf => ({
        id: `customField${cf.customFieldTemplate.id}`,
        label: cf.customFieldTemplate.defaultLabel || `Custom ${cf.customFieldTemplate.id}`,
        inputType: "text",
        readOnly: false,
        isCustomField: true,
      }));
  }, [fieldSettings, data]);

  const enabledColumns = useMemo(() => {
    if (!fieldSettings) return [];
    const standardCols = assetsTableConfig.fields
      .filter(f => {
        if (SKIP_FIELD_IDS.has(f.id) || f.system || f.alwaysVisible) return false;
        return isFieldSettingEnabled(fieldSettings[snakeCase(f.attrName || f.id)]);
      })
      .map(f => ({
        id: f.id,
        attrName: f.attrName,
        label: getFieldLabel(f),
        inputType: f.inputType || "text",
        readOnly: Boolean(f.unEditable),
        isCustomField: false,
      }));
    return [...standardCols, ...customFieldColumns];
  }, [fieldSettings, customFieldColumns]);

  const orderedColumns = useMemo(() => {
    const fieldOrder = new Map(tableProFields.map((f, i) => [f.id, i]));
    return [...enabledColumns].sort((a, b) =>
      (fieldOrder.get(a.id) ?? Infinity) - (fieldOrder.get(b.id) ?? Infinity)
    );
  }, [enabledColumns, tableProFields]);

  const fieldsForReorder = useMemo(() => {
    const enabledMap = new Map(orderedColumns.map(c => [c.id, c]));
    const existingIds = new Set(tableProFields.map(f => f.id));
    const fields = tableProFields.map(f => {
      const col = enabledMap.get(f.id);
      return col ? { ...f, visible: true, label: col.label } : f;
    });
    for (const c of orderedColumns) {
      if (c.isCustomField && !existingIds.has(c.id)) {
        fields.push({ id: c.id, label: c.label, visible: true });
      }
    }
    return fields;
  }, [tableProFields, orderedColumns]);

  const reorderHiddenIds = useMemo(() => {
    const visibleIds = new Set(orderedColumns.map(c => c.id));
    const hidden = new Set(SKIP_FIELD_IDS);
    fieldsForReorder.forEach(f => {
      if (!visibleIds.has(f.id)) hidden.add(f.id);
    });
    return hidden;
  }, [orderedColumns, fieldsForReorder]);

  const [localWidths, setLocalWidths] = useState(() => {
    const saved = Object.fromEntries(
      tableProFields.filter(f => f.width).map(f => [f.id, f.width])
    );
    return { ...COLUMN_WIDTHS, ...saved };
  });

  const getColWidth = colId => localWidths[colId] ?? null;

  const handleColumnResizing = useCallback((colIndex, delta) => {
    const colId = orderedColumns[colIndex]?.id;
    if (!colId) return;
    setLocalWidths(prev => ({
      ...prev,
      [colId]: Math.max(MIN_COL_WIDTH, (prev[colId] ?? DEFAULT_COL_WIDTH) + delta),
    }));
  }, [orderedColumns]);

  const handleColumnResizeStop = useCallback(() => {
    const updatedFields = tableProFields.map(f => ({
      ...f,
      width: localWidths[f.id] ?? f.width,
    }));
    updateTableSettings({ fields: updatedFields });
  }, [tableProFields, localWidths, updateTableSettings]);

  const handleCellSave = useCallback((entityId, fieldId, value) => {
    setEntities(prev => {
      const updated = prev.map(e => e.id === entityId ? { ...e, [fieldId]: value } : e);

      if (fieldId !== "validationQty") {
        if (fieldId.startsWith("customField")) {
          const entity = updated.find(e => e.id === entityId);
          const templateId = getCustomFieldTemplateId(fieldId);
          const existing = findExistingCustomField(entity, templateId);
          const payload = createCustomFieldPayload(templateId, value, existing);
          bulkUpdateAssets({ variables: { input: [{ id: parseInt(entityId), ...payload }] } });
        } else {
          bulkUpdateAssets({ variables: { input: [{ id: parseInt(entityId), [fieldId]: value }] } });
        }
      }

      return updated;
    });
  }, [bulkUpdateAssets]);

  const handleSearch = () => setSearchTerm(searchInput);

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSort = field => {
    const snaked = snakeCase(field);
    if (sortField === snaked) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(snaked);
      setSortDir("asc");
    }
  };

  const [addingNew, setAddingNew] = useState(false);

  const handleAddNew = useCallback(async () => {
    if (addingNew) return;
    setAddingNew(true);
    try {
      const { data: skuData } = await createSku({
        variables: { projectId: project.id, input: {} },
      });
      const newAsset = skuData?.createSku?.asset;
      const errors = skuData?.createSku?.errors;
      if (errors?.length) {
        notifyError(errors.map(e => e.message).join(", "));
        return;
      }
      if (!newAsset) {
        notifyError("Failed to create item.");
        return;
      }
      await addShipmentItem({
        variables: { assetId: newAsset.id, shipmentId, quantity: 1 },
      });
      const flattened = flattenSku(newAsset);
      pinnedItemsRef.current = [flattened, ...pinnedItemsRef.current];
      setEntities(prev => [flattened, ...prev]);
      notifySuccess("New item added");
    } catch (err) {
      console.error("Add new SKU error:", err);
      notifyError("Something went wrong. Please try again.");
    } finally {
      setAddingNew(false);
    }
  }, [addingNew, createSku, addShipmentItem, flattenSku, project.id, shipmentId, notifySuccess, notifyError]);

  const loadMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;
  const loadingRef = useRef(false);
  loadingRef.current = loading;
  const metadataRef = useRef({});
  metadataRef.current = metadata;

  const handleLoadMore = useCallback(() => {
    if (loadMoreRef.current || !hasMoreRef.current || loadingRef.current) return;
    loadMoreRef.current = true;
    setLoadingMore(true);

    fetchMore({
      variables: { ...queryVars, page: metadataRef.current.currentPage + 1 },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const existingIds = new Set(
          previousResult.assetsCollection.collection.map(item => item.id)
        );
        const newItems = fetchMoreResult.assetsCollection.collection.filter(
          item => !existingIds.has(item.id)
        );

        if (newItems.length === 0) return previousResult;

        return {
          ...previousResult,
          assetsCollection: {
            ...previousResult.assetsCollection,
            collection: [...previousResult.assetsCollection.collection, ...newItems],
            metadata: fetchMoreResult.assetsCollection.metadata,
          },
        };
      },
    }).finally(() => {
      loadMoreRef.current = false;
      setLoadingMore(false);
    });
  }, [fetchMore, queryVars]);

  const containerRef = useRef(null);
  const handleLoadMoreRef = useRef(handleLoadMore);
  handleLoadMoreRef.current = handleLoadMore;

  const scrollListenerRef = useRef(null);

  const attachScrollListener = useCallback((el) => {
    if (scrollListenerRef.current) {
      scrollListenerRef.current.el.removeEventListener("scroll", scrollListenerRef.current.fn);
      scrollListenerRef.current = null;
    }
    if (!el) return;

    const fn = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
        handleLoadMoreRef.current();
      }
    };
    el.addEventListener("scroll", fn, { passive: true });
    scrollListenerRef.current = { el, fn };
  }, []);

  const tableContainerRef = useCallback((node) => {
    containerRef.current = node;
    attachScrollListener(node);
  }, [attachScrollListener]);

  const [selectedCoords, setSelectedCoords] = useState([]);
  const [editTrigger, setEditTrigger] = useState(null);
  const selectedCoordsRef = useRef([]);
  selectedCoordsRef.current = selectedCoords;

  const totalCols = orderedColumns.length + 1;

  const handleNavRight = useCallback(() => {
    const [row, col] = selectedCoordsRef.current;
    if (row === undefined) return;
    setEditTrigger(null);
    if (col + 1 < totalCols) {
      setSelectedCoords([row, col + 1]);
    } else if (row + 1 < entities.length) {
      setSelectedCoords([row + 1, 0]);
    }
  }, [totalCols, entities.length]);

  const handleNavLeft = useCallback(() => {
    const [row, col] = selectedCoordsRef.current;
    if (row === undefined) return;
    setEditTrigger(null);
    if (col - 1 >= 0) {
      setSelectedCoords([row, col - 1]);
    } else if (row - 1 >= 0) {
      setSelectedCoords([row - 1, totalCols - 1]);
    }
  }, [totalCols]);

  const handleNavDown = useCallback(() => {
    const [row, col] = selectedCoordsRef.current;
    if (row === undefined) return;
    setEditTrigger(null);
    if (row + 1 < entities.length) setSelectedCoords([row + 1, col]);
  }, [entities.length]);

  const handleNavUp = useCallback(() => {
    const [row, col] = selectedCoordsRef.current;
    if (row === undefined) return;
    setEditTrigger(null);
    if (row - 1 >= 0) setSelectedCoords([row - 1, col]);
  }, []);

  const canNavigate = selectedCoords.length === 2;

  useNavigationEffect(containerRef, canNavigate ? {
    right: handleNavRight,
    left: handleNavLeft,
    down: handleNavDown,
    up: handleNavUp,
  } : {});

  useEffect(() => {
    if (!canNavigate) return;
    const listener = event => {
      if (event.target.getAttribute("data-skip-navigation-effect")) return;
      if (event.metaKey || event.ctrlKey) return;
      const char = String.fromCharCode(event.keyCode);
      if (!/[a-zA-Z0-9-_ ]/.test(char)) return;
      event.preventDefault();
      setEditTrigger({ key: event.key, ts: Date.now() });
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [canNavigate]);

  useEffect(() => {
    const listener = event => {
      if (event.key !== "Enter" || selectedCoordsRef.current.length !== 2) return;
      if (event.target.getAttribute("data-skip-navigation-effect")) return;
      event.preventDefault();
      const [row, col] = selectedCoordsRef.current;
      const el = containerRef.current?.querySelector(`[data-nav-cell="${row}-${col}"]`);
      if (el) el.click();
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  useEffect(() => {
    if (selectedCoords.length !== 2) return;
    const [row, col] = selectedCoords;
    const el = containerRef.current?.querySelector(`[data-nav-cell="${row}-${col}"]`);
    if (el) el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selectedCoords]);

  const handleCellSelect = useCallback((rowIndex, colIndex) => {
    setEditTrigger(null);
    setSelectedCoords([rowIndex, colIndex]);
  }, []);

  const isCellSelected = (rowIndex, colIndex) =>
    selectedCoords[0] === rowIndex && selectedCoords[1] === colIndex;

  const lastSelectedIdRef = useRef(null);

  const handleToggle = useCallback((id, event) => {
    setSelectedIds(prev => {
      const next = new Set(prev);

      if (event?.shiftKey && lastSelectedIdRef.current) {
        const ids = entities.map(s => s.id);
        const start = ids.indexOf(lastSelectedIdRef.current);
        const end = ids.indexOf(id);

        if (start !== -1 && end !== -1) {
          const [min, max] = start < end ? [start, end] : [end, start];
          for (let i = min; i <= max; i++) next.add(ids[i]);
          lastSelectedIdRef.current = id;
          return next;
        }
      }

      next.has(id) ? next.delete(id) : next.add(id);
      lastSelectedIdRef.current = id;
      return next;
    });
  }, [entities]);

  const allSelected = entities.length > 0 && entities.every(s => selectedIds.has(s.id));

  const handleToggleAll = () => {
    lastSelectedIdRef.current = null;
    setSelectedIds(prev => {
      const next = new Set(prev);
      entities.forEach(s => allSelected ? next.delete(s.id) : next.add(s.id));
      return next;
    });
  };

  const handleValidate = async () => {
    if (selectedIds.size === 0) return;
    setValidating(true);

    const toValidate = entities.filter(s => selectedIds.has(s.id));

    try {
      await Promise.all(
        toValidate.map(async (sku, index) => {
          const isLast = index === toValidate.length - 1;
          const qty = sku.validationQty || 1;

          const { assetId, validationListValidated, validationQty, attachments, ...rawInput } = fetchAssetValues(sku);
          const assetInput = Object.fromEntries(
            Object.entries(rawInput).filter(([, v]) => v !== "" && v !== null && v !== undefined)
          );
          if (assetInput.rackLocations) {
            assetInput.rackLocations = assetInput.rackLocations.map(rl => ({
              ...rl,
              sub_qty: qty,
              shipment_id: shipmentCode,
            }));
          }
          const createResult = await createAsset({
            variables: { projectId: project.id, input: assetInput, skipNotifications: true, skipRefreshCache: !isLast },
          });
          const newAsset = createResult.data.createAsset.asset;

          await addShipmentItem({
            variables: {
              assetId: newAsset.id,
              shipmentId,
              quantity: qty,
              rackLocations: newAsset.rackLocations,
            },
          });

          await updateAsset({
            variables: {
              id: sku.id,
              input: { validationListValidated: new Date().toISOString() },
            },
          });
        })
      );

      notifySuccess(`${toValidate.length} item${toValidate.length === 1 ? "" : "s"} validated`);
      await apolloClient.refetchQueries({ include: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }] });
      onClose();
    } catch (err) {
      console.error("Manifest validation error:", err);
      notifyError("Something went wrong during validation. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const skeletonColCount = orderedColumns.length + 2;

  return (
    <>
    <Dialog open fullWidth maxWidth="lg" onClose={onClose} sx={{ ...fullWidthDialog, "& .MuiDialog-paper": { height: "75vh" } }}>
      <DialogTitle sx={{ textAlign: "center", bgcolor: "#f4f6f8", fontWeight: 500, fontSize: 20, color: "text.primary" }}>
       Items Remaining to Verify
      </DialogTitle>
      <DialogContent sx={{ background: "#f4f6f8", px: 3, pb: 1.5, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              size="small"
              sx={{ width: 360 }}
              placeholder="Search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              inputProps={{ "data-skip-navigation-effect": "true" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton size="small" onClick={handleSearch} edge="start">
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* TODO: Re-enable once Add New flow is finalized
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddNew}
              disabled={addingNew}
              startIcon={addingNew ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Add New
            </Button>
            */}
          </Box>
          <Button
            onClick={() => setReorderOpen(true)}
            startIcon={<SyncAltIcon sx={{ transform: "rotate(90deg)" }} fontSize="small" />}
            sx={{ whiteSpace: "nowrap", textTransform: "none", color: "text.secondary" }}
          >
            Reorder Columns
          </Button>
        </Box>
        {loading && !loadingMore ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : entities.length === 0 && !loading ? (
          <Box py={4} textAlign="center">
            <Typography color="textSecondary">
              {searchTerm ? "No items match your search." : "All items have been validated."}
            </Typography>
          </Box>
        ) : (
          <TableContainer ref={tableContainerRef} sx={{ flex: 1, overflow: "auto", minHeight: 0, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow className="resizableWrap">
                  <TableCell padding="checkbox" sx={{ ...stickyCheckboxSx, bgcolor: "background.paper", zIndex: 3 }}>
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && !allSelected}
                      checked={allSelected}
                      onClick={handleToggleAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ ...stickyQtySx, bgcolor: "background.paper", fontWeight: 600, zIndex: 3, lineHeight: 1.2 }}>
                    Verified<br />QTY
                  </TableCell>
                  {orderedColumns.map((col, colIndex) => {
                    const w = getColWidth(col.id);
                    const colSortKey = snakeCase(col.attrName || col.id);
                    return (
                      <TableCell key={col.id} sx={{ bgcolor: "background.paper", fontWeight: 600, ...(w && { width: w, minWidth: w }) }}>
                        <TableSortLabel
                          active={sortField === colSortKey}
                          direction={sortField === colSortKey ? sortDir : "asc"}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                        <ResizeBorder
                          index={colIndex}
                          onResizing={handleColumnResizing}
                          onStopReisizing={handleColumnResizeStop}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {entities.map((sku, rowIndex) => {
                  const isSelected = selectedIds.has(sku.id);
                  return (
                    <TableRow key={sku.id} hover selected={isSelected}>
                      <TableCell padding="checkbox" sx={theme => ({ ...stickyCheckboxSx, background: stickyBg(theme, isSelected) })}>
                        <Checkbox
                          checked={isSelected}
                          onClick={e => {
                            e.stopPropagation();
                            handleToggle(sku.id, e);
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        data-nav-cell={`${rowIndex}-0`}
                        onClick={() => handleCellSelect(rowIndex, 0)}
                        sx={theme => ({
                          ...stickyQtySx,
                          py: 0.5,
                          borderRightColor: theme.palette.divider,
                          background: stickyBg(theme, isSelected),
                          ...(isCellSelected(rowIndex, 0) && { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 }),
                        })}
                      >
                        <EditableCell
                          value={sku.validationQty}
                          inputType="number"
                          onSave={val => handleCellSave(sku.id, "validationQty", val)}
                          activateWithKey={isCellSelected(rowIndex, 0) ? editTrigger : null}
                        />
                      </TableCell>
                      {orderedColumns.map((col, colIndex) => {
                        const w = getColWidth(col.id);
                        const navCol = colIndex + 1;
                        return (
                          <TableCell
                            key={col.id}
                            data-nav-cell={`${rowIndex}-${navCol}`}
                            onClick={() => handleCellSelect(rowIndex, navCol)}
                            sx={{
                              py: 0.5,
                              ...(w ? { width: w, minWidth: w } : { minWidth: 100 }),
                              ...(isCellSelected(rowIndex, navCol) && { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 }),
                            }}
                          >
                            <Box>
                              {col.id === "itemId" ? (
                                <Box
                                  onClick={() => setDetailEntity(sku)}
                                  sx={{ cursor: "pointer", color: "primary.main", px: 0.5, "&:hover": { textDecoration: "underline" } }}
                                >
                                  {sku.itemId || "—"}
                                </Box>
                              ) : col.readOnly ? (
                                <Box sx={{ px: 0.5 }}>{(sku[col.id] != null && sku[col.id] !== "") ? String(sku[col.id]) : "—"}</Box>
                              ) : (
                                <EditableCell
                                  value={sku[col.id]}
                                  inputType={col.inputType}
                                  onSave={val => handleCellSave(sku.id, col.id, val)}
                                  activateWithKey={isCellSelected(rowIndex, navCol) ? editTrigger : null}
                                />
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {loadingMore && Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from({ length: skeletonColCount }).map((_, j) => (
                      <TableCell key={j} sx={{ py: 1 }}>
                        <Skeleton variant="rectangular" height={20} width="60%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, background: "#f4f6f8", justifyContent: "space-between" }}>
        <Typography variant="body2" color="textSecondary">
          {entities.length} of {metadata.totalCount ?? "…"} item{metadata.totalCount !== 1 ? "s" : ""} remaining
          {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
        </Typography>
        <Button
          variant="contained"
          onClick={handleValidate}
          disabled={validating || selectedIds.size === 0}
          startIcon={validating ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ textTransform: "none" }}
        >
          Validate {selectedIds.size > 0 ? selectedIds.size : ""}{" "}
          {selectedIds.size === 1 ? "Item" : "Items"}
        </Button>
      </DialogActions>
    </Dialog>

    {detailEntity && (
      <AssetsCollectionProvider skipQuery>
        <EditAssetProvider>
          <AssetDetailsModal
            entity={detailEntity}
            onClose={() => { setDetailEntity(null); refetch(); }}
          />
        </EditAssetProvider>
      </AssetsCollectionProvider>
    )}

    <Dialog open={reorderOpen} maxWidth="sm" fullWidth onClose={() => setReorderOpen(false)}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#f4f6f8",
        }}
      >
        <Box width={48} />
        <Typography color="textSecondary" variant="h5" fontWeight={500} align="center">
          Reorder Columns
        </Typography>
        <IconButton onClick={() => setReorderOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ background: "#f4f6f8" }}>
        <Order fields={fieldsForReorder} onChange={updateTableSettings} reorderFields hideFieldIds={reorderHiddenIds} />
      </DialogContent>
    </Dialog>
    </>
  );
}
